export const MIDI_MIN_PITCH = 0;
export const MIDI_MAX_PITCH = 127;

export const clampMidiPitch = (pitch) => Math.max(
  MIDI_MIN_PITCH,
  Math.min(MIDI_MAX_PITCH, Number.isFinite(Number(pitch)) ? Number(pitch) : MIDI_MIN_PITCH),
);

export const filenameRootPitch = (filename) => {
  const stem = String(filename || "").replace(/\.[^.]+$/, "");
  const matches = [...stem.matchAll(/(?:^|[^A-Ga-g0-9])([A-Ga-g])([#b]?)(-?\d)(?=$|[^0-9])/g)];
  if (!matches.length) return null;
  const [, note, accidental, octave] = matches.at(-1);
  const semitones = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  const accidentalOffset = accidental === "#" ? 1 : accidental === "b" ? -1 : 0;
  return clampMidiPitch((Number(octave) + 1) * 12 + semitones[note.toUpperCase()] + accidentalOffset);
};

export const inferRootPitchFromFilename = (filename, fallbackPitch = 48) => (
  filenameRootPitch(filename) ?? clampMidiPitch(fallbackPitch)
);

export const oneKeyMappingPitches = (sampleList, rootPitch, { inferFilenamePitch = true } = {}) => {
  const sampleCount = Math.min(128, sampleList.length);
  const startPitch = Math.max(MIDI_MIN_PITCH, Math.min(MIDI_MAX_PITCH - Math.max(0, sampleCount - 1), clampMidiPitch(rootPitch)));
  return sampleList.slice(0, 128).map((sample, index) => (
    inferFilenamePitch
      ? inferRootPitchFromFilename(sample.name, startPitch + index)
      : startPitch + index
  ));
};

export const fullChromaticPitchRange = (rootPitch) => ({
  rootPitch: clampMidiPitch(rootPitch),
  lowPitch: MIDI_MIN_PITCH,
  highPitch: MIDI_MAX_PITCH,
});

export const collapseMappingsToRoot = (mappings) => mappings.map((mapping) => {
  const rootPitch = clampMidiPitch(mapping.rootPitch);
  return {
    ...mapping,
    rootPitch,
    lowPitch: rootPitch,
    highPitch: rootPitch,
  };
});

export const velocityLayerRange = (layer, layerCount = 4) => {
  const count = Math.max(1, Math.min(4, Math.round(Number(layerCount) || 1)));
  const index = Math.max(1, Math.min(count, Math.round(Number(layer) || 1)));
  return {
    velocityLow: Math.floor(((index - 1) * 127) / count) + 1,
    velocityHigh: Math.floor((index * 127) / count),
  };
};

export const stackMappingsAsVelocityLayers = (mappings, selectedMappingIds, anchorMappingId = null) => {
  const selectedIds = new Set(selectedMappingIds || []);
  const targets = mappings.filter((mapping) => selectedIds.has(mapping.id)).slice(0, 4);
  if (targets.length < 2) return mappings;
  const anchor = targets.find((mapping) => mapping.id === anchorMappingId) || targets[0];
  const layerById = new Map(targets.map((mapping, index) => [mapping.id, index + 1]));
  return mappings.map((mapping) => {
    const layer = layerById.get(mapping.id);
    return layer ? {
      ...mapping,
      rootPitch: anchor.rootPitch,
      lowPitch: anchor.lowPitch,
      highPitch: anchor.highPitch,
      ...velocityLayerRange(layer, targets.length),
    } : mapping;
  });
};

export const scaleSourceBpm = (sourceBpm, multiplier = 1) => {
  const numericBpm = Number(sourceBpm);
  const normalizedBpm = Number.isFinite(numericBpm) && numericBpm > 0 ? numericBpm : 120;
  const scaledBpm = Math.max(20, Math.min(300, normalizedBpm * multiplier));
  return Number(scaledBpm.toFixed(3));
};

export const normalizeChokeTargetPitches = (targets, limit = 16) => [...new Set(
  (Array.isArray(targets) ? targets : [])
    .map(Number)
    .filter((pitch) => Number.isInteger(pitch) && pitch >= MIDI_MIN_PITCH && pitch <= MIDI_MAX_PITCH),
)].slice(0, limit);

const sampleFilenameKey = (filename) => String(filename || "").trim().toLowerCase();

export const planSampleFileDrop = (
  currentSamples,
  droppedFiles,
  { allowExistingReferences = false, maxSamples = 128 } = {},
) => {
  const samples = Array.isArray(currentSamples) ? currentSamples : [];
  const files = Array.from(droppedFiles || []);
  const samplesByName = new Map(samples.map((sample) => [sampleFilenameKey(sample.name), sample]));
  let availableSlots = Math.max(0, maxSamples - samples.length);
  const added = [];
  const reattached = [];
  const reused = [];
  const resolvedSamples = [];
  let duplicates = 0;

  files.forEach((file) => {
    const filenameKey = sampleFilenameKey(file?.name);
    const existing = samplesByName.get(filenameKey);
    if (existing) {
      if (!existing.file && !existing.uploaded && Number(existing.size) === Number(file?.size)) {
        const reattachedSample = {
          ...existing,
          file,
          uploaded: false,
          assetId: null,
          sha256: null,
          uploadedAt: null,
        };
        reattached.push(reattachedSample);
        resolvedSamples.push(reattachedSample);
        samplesByName.set(filenameKey, reattachedSample);
      } else if (allowExistingReferences) {
        reused.push(existing);
        resolvedSamples.push(existing);
      } else {
        duplicates += 1;
      }
      return;
    }

    if (availableSlots <= 0) return;
    const sample = {
      id: null,
      name: file.name,
      size: file.size,
      file,
      uploaded: false,
      assetId: null,
      sha256: null,
      uploadedAt: null,
    };
    added.push(sample);
    resolvedSamples.push(sample);
    samplesByName.set(filenameKey, sample);
    availableSlots -= 1;
  });

  return { added, reattached, reused, resolvedSamples, duplicates };
};
